import { unwrapResult } from '~/modules/query/base';
import { notificationApi } from '~/modules/query/notification';
import type {
  ProviderKey,
  ProviderKeyInput,
  RevealedCredentials,
} from '~/modules/query/notification/types';

const seg = encodeURIComponent;

export const providerKeysApi = notificationApi.injectEndpoints({
  endpoints: (builder) => ({
    listProviderKeys: builder.query<ProviderKey[], void>({
      query: () => ({ url: '/provider-keys', method: 'GET' }),
      transformResponse: unwrapResult<ProviderKey[]>,
      providesTags: (result) => [
        'NotificationProviderKey',
        ...(result ?? []).map((k) => ({ type: 'NotificationProviderKey' as const, id: k.id })),
      ],
    }),
    getProviderKey: builder.query<ProviderKey, string>({
      query: (id) => ({ url: `/provider-keys/${seg(id)}`, method: 'GET' }),
      transformResponse: unwrapResult<ProviderKey>,
      providesTags: (_result, _error, id) => [{ type: 'NotificationProviderKey', id }],
    }),
    // A one-time reveal, so the plaintext stays in the page's local state rather than in the cache.
    revealProviderKeyCredentials: builder.mutation<RevealedCredentials, string>({
      query: (id) => ({ url: `/provider-keys/${seg(id)}/reveal`, method: 'POST' }),
      transformResponse: unwrapResult<RevealedCredentials>,
    }),
    createProviderKey: builder.mutation<ProviderKey, ProviderKeyInput>({
      query: (data) => ({ url: '/provider-keys', method: 'POST', data }),
      transformResponse: unwrapResult<ProviderKey>,
      invalidatesTags: ['NotificationProviderKey'],
    }),
    updateProviderKey: builder.mutation<ProviderKey, { id: string } & ProviderKeyInput>({
      query: ({ id, ...data }) => ({ url: `/provider-keys/${seg(id)}`, method: 'PUT', data }),
      transformResponse: unwrapResult<ProviderKey>,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'NotificationProviderKey', id }],
    }),
    deleteProviderKey: builder.mutation<{ deleted: true; id: string }, string>({
      query: (id) => ({ url: `/provider-keys/${seg(id)}`, method: 'DELETE' }),
      transformResponse: unwrapResult<{ deleted: true; id: string }>,
      invalidatesTags: (_result, _error, id) => [{ type: 'NotificationProviderKey', id }],
    }),
  }),
});

export const {
  useListProviderKeysQuery,
  useGetProviderKeyQuery,
  useRevealProviderKeyCredentialsMutation,
  useCreateProviderKeyMutation,
  useUpdateProviderKeyMutation,
  useDeleteProviderKeyMutation,
} = providerKeysApi;
